import { filterToCondition } from "@/shared/components/filters/filterToCondition";
import type { DataTableSort } from "@/shared/foundation/primitives/Table";
import type { Condition } from "@/types";

import type { ActiveFilters } from "./FilterModel";

/**
 * Three pieces of submission state derived from the UI's `ActiveFilters`
 * union, the table's current sort, and the parsed RoboQL spec when the
 * user is in roboql mode.
 */
export interface QuerySubmission {
  /**
   * Wire-shape condition for the user's current filter intent. Hands
   * straight into `composeQueryBody({ scopeCondition, filterCondition, sort })`.
   * `undefined` when the user has no filter (or has only deselected
   * filters in structured mode).
   */
  filterCondition: Condition | undefined;
  /**
   * Resolved sort under the three-way precedence rule:
   *   1. UI sort (the user clicked a column header) — wins outright.
   *   2. No UI sort + RoboQL spec carries `sort_by` — fall back to the
   *      parsed sort. The convert endpoint's projection semantic
   *      guarantees `spec.sort_by` is set only when the user actually
   *      typed `SORT BY`, so this branch is unambiguous.
   *   3. Neither — `null`. `composeQueryBody` omits `sort_by` /
   *      `sort_direction` from the body and the backend's per-target
   *      default kicks in inside `submit_structured_query`.
   */
  sort: DataTableSort | null;
  /**
   * Total-results cap
   */
  maxResults: number | undefined;
}

/**
 * Translate the UI layer's filter state into the inputs the domain
 * layer needs to assemble a `SearchQueryBody`. **Single source of truth
 * for the three-way sort precedence rule** and for the UI→Condition
 * collapse of the discriminated union.
 */
export function activeFiltersToQuerySubmission(args: {
  filters: ActiveFilters;
  sort: DataTableSort | null;
}): QuerySubmission {
  const { filters, sort } = args;

  let filterCondition: Condition | undefined;
  if (filters.type === "structured") {
    filterCondition = filterToCondition(filters.filters, {
      matchMode: filters.matchMode,
    });
  } else if (filters.type === "roboql") {
    filterCondition = filters.spec.condition;
  }

  // Three-way sort precedence — see `QuerySubmission.sort` docstring.
  // The string `"DESC"` check matches the on-wire `SortDirection` enum
  // exactly; lower-casing it produces the `DataTableSort.direction`
  // discriminant used by the table layer.
  const resolvedSort: DataTableSort | null =
    sort ??
    (filters.type === "roboql" && filters.spec.sort_by
      ? {
          columnId: filters.spec.sort_by,
          direction: filters.spec.sort_direction === "DESC" ? "desc" : "asc",
        }
      : null);

  const maxResults: number | undefined =
    filters.type === "roboql" && filters.spec.max_results
      ? filters.spec.max_results
      : undefined;

  return {
    filterCondition,
    sort: resolvedSort,
    maxResults,
  };
}
